/* iter55p5 — Public unauth demo route: /demo/genre-preview.
 *
 * Renders the LIVE seeded Dark Romance 25 template's GenreVariantPicker
 * so we can screenshot it for landing-page marketing GIFs. No auth,
 * no writer session, no OAuth — just a public fetch of the public
 * `/api/starter-templates` endpoint filtered to `dr25v2`.
 *
 * URL knobs (for scripted screenshotting):
 *   ?slug=mafia            → single-select preset
 *   ?slug=stalker
 *   ?slug=mafia,monster    → blend preset (later wins)
 *   ?blend=1               → force blend mode toggle on
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import GenreVariantPicker from '@/components/dialogs/GenreVariantPicker';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function GenrePreviewDemo() {
  const [tpl, setTpl] = useState(null);
  const [err, setErr] = useState('');

  // Read query knobs for deterministic screenshots.
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialSlugs = useMemo(() => {
    const s = params.get('slug');
    if (!s) return null;
    return s.split(',').map((x) => x.trim()).filter(Boolean);
  }, [params]);

  const [selectedSlugs, setSelectedSlugs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/starter-templates`).then((r) => {
      if (cancelled) return;
      const templates = Array.isArray(r.data) ? r.data : (r.data?.templates || []);
      const dr = templates.find((t) => t?.id === 'dr25v2');
      if (!dr) {
        setErr("Template 'dr25v2' not found. Run `python -m app.scripts.seed_dr25v2` first.");
        return;
      }
      setTpl(dr);
      const slugs = Object.keys(dr.genre_variants || {});
      const seed = initialSlugs
        ? initialSlugs.filter((s) => slugs.includes(s))
        : (slugs[0] ? [slugs[0]] : []);
      setSelectedSlugs(seed.length ? seed : (slugs[0] ? [slugs[0]] : []));
    }).catch((e) => setErr(e?.message || 'Failed to fetch starter templates.'));
    return () => { cancelled = true; };
  }, [initialSlugs]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 px-6 py-10 text-neutral-100"
         data-testid="genre-preview-demo-root">
      <div className="mx-auto max-w-4xl">
        <header className="mb-6">
          <p className="text-[10.5px] uppercase tracking-[0.32em] text-amber-400/80">
            The Loom · Genre Variants Demo
          </p>
          <h1 className="mt-1 font-serif text-[34px] leading-tight text-amber-100">
            One beat map. Any genre.
          </h1>
          <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-neutral-300">
            The 25-chapter Dark Romance framework ships with placeholder tokens
            (<code className="rounded-sm bg-white/10 px-1">{'{{external_threat}}'}</code>,
            <code className="rounded-sm bg-white/10 px-1">{'{{territory}}'}</code>,
            <code className="rounded-sm bg-white/10 px-1">{'{{grand_gesture}}'}</code>).
            Flip a genre — every beat reflavours instantly. Blend two — the later
            genre overrides the earlier on token collision.
          </p>
        </header>

        {err && (
          <div className="rounded-md border border-rose-400/50 bg-rose-500/10 p-3 text-[12px] text-rose-200"
               data-testid="genre-preview-demo-error">
            {err}
          </div>
        )}

        {!tpl && !err && (
          <div className="rounded-md border border-white/10 bg-white/5 p-3 text-[12px] text-neutral-400">
            Loading beat map from <code>/api/starter-templates</code>…
          </div>
        )}

        {tpl && (
          <div className="rounded-lg border border-white/10 bg-neutral-100 p-4 text-neutral-900 shadow-2xl"
               data-testid="genre-preview-demo-card">
            <GenreVariantPicker
              template={tpl}
              values={{
                hero_name: 'Roman Volkov',
                heroine_name: 'Isla Moreau',
              }}
              selectedSlugs={selectedSlugs}
              setSelectedSlugs={setSelectedSlugs}
            />
          </div>
        )}

        <p className="mt-6 text-[10.5px] text-neutral-500">
          Public demo route · rendered from <code>/api/starter-templates</code> ·
          no auth, no writer session. Screenshot-friendly for landing / launch
          collateral.
        </p>
      </div>
    </div>
  );
}
